import { seesEveryPage, type Me, type RoleKey } from '@myrewards/contracts';
import type { QueryClient } from '@tanstack/react-query';
import { data, isRouteErrorResponse } from 'react-router';
import { isApiError, type ApiError } from '../api';
import { requireMe } from '../session/loaders';

/** What a page someone can't open tells the error page: who the page is for, when known. */
export interface NoAccessFacts {
  /** "Payroll", "the Stock Admin team"; null when the page can't say. */
  for: string | null;
  /**
   * The server's own words when it refused with a reason ("Preview access has ended. Exit Preview
   * to continue."): shown instead of who the page is for, which would be wrong then.
   */
  detail?: string;
}

/**
 * A page this person can't open (APP-134 §5): a 403 the route's error page shows as "No access",
 * naming who the page is for. Pages aren't secret (every address ships in the app and is in Help),
 * so saying so hides nothing. Never for a record opened by its id: a record outside someone's
 * scope stays "not found", the same as one that doesn't exist, so nobody learns which exist.
 */
export function noAccess(forWhom: string | null = null, refused?: ApiError) {
  // The server's reason, when it gave one, says more than who the page is for.
  const detail = refused?.problem?.detail;
  return data<NoAccessFacts>(detail === undefined ? { for: forWhom } : { for: forWhom, detail }, {
    status: 403,
    statusText: 'Forbidden',
  });
}

/**
 * Whether a route failed for want of access, with who the page is for: a `noAccess` response, or
 * a read the server refused (403) on its way in.
 */
export function noAccessFacts(error: unknown): NoAccessFacts | null {
  if (isApiError(error)) {
    if (error.status !== 403) return null;
    const detail = error.problem?.detail;
    return detail === undefined ? { for: null } : { for: null, detail };
  }
  if (!isRouteErrorResponse(error) || error.status !== 403) return null;
  const facts = error.data as Partial<NoAccessFacts> | null | undefined;
  const forWhom = typeof facts?.for === 'string' ? facts.for : null;
  return typeof facts?.detail === 'string'
    ? { for: forWhom, detail: facts.detail }
    : { for: forWhom };
}

/** Whether this person opens a page for these roles: one of them, or a System Admin (APP-134). */
export function opensPage(me: Pick<Me, 'roles'>, roles: readonly RoleKey[]): boolean {
  const held = me.roles.map((r) => r.role);
  return seesEveryPage(held) || roles.some((role) => held.includes(role));
}

/** A role's page: its roles and System Admins open it; anyone else sees No access. */
export async function requirePage(
  queryClient: QueryClient,
  request: Request,
  roles: readonly RoleKey[],
  forWhom: string,
): Promise<Me> {
  const me = await requireMe(queryClient, request);
  // eslint-disable-next-line @typescript-eslint/only-throw-error -- React Router shows a thrown response as a route error.
  if (!opensPage(me, roles)) throw noAccess(forWhom);
  return me;
}
